import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  Folder,
  Link2,
  Pencil,
  Plus,
  Trash2,
  Video,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { MediaNodeDialog } from "@/features/admin/components/media-node-dialog";
import { deleteMedia, listMedia, reorderMedia, symlinkMedia } from "@/features/admin/api";
import type { MediaAsset, MediaAssetType } from "@/features/admin/types";

const TYPE_ICON: Record<MediaAssetType, typeof Folder> = {
  DIR: Folder,
  VIDEO: Video,
  PDF: FileText,
  LINK: Link2,
  TEXT: FileText,
  SLINK: Copy,
};

interface TreeNode extends MediaAsset {
  children: TreeNode[];
}

function buildTree(items: MediaAsset[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  items.forEach((i) => byId.set(i.id, { ...i, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    if (node.parent && byId.has(node.parent)) byId.get(node.parent)!.children.push(node);
    else roots.push(node);
  });
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.displaySeq - b.displaySeq);
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

export default function MediaPage() {
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [nodeDialog, setNodeDialog] = useState<{ node: MediaAsset | null; parentId: string | null } | null>(null);
  const [deleting, setDeleting] = useState<MediaAsset | null>(null);
  const [symlinkFor, setSymlinkFor] = useState<MediaAsset | null>(null);
  const [symlinkTargets, setSymlinkTargets] = useState<Set<string>>(new Set());

  async function refresh() {
    setLoading(true);
    try {
      setItems(await listMedia());
    } catch {
      toast.error("Couldn't load the media library.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const tree = useMemo(() => buildTree(items), [items]);
  const folders = useMemo(() => items.filter((i) => i.assetType === "DIR"), [items]);

  function toggle(id: string) {
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function moveSibling(siblings: TreeNode[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[index], reordered[target]] = [reordered[target]!, reordered[index]!];
    try {
      await reorderMedia(
        reordered.map((s, i) => ({ id: s.id, parent: s.parent, displaySeq: i })),
      );
      await refresh();
    } catch {
      toast.error("Couldn't reorder.");
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteMedia(deleting.id);
      setDeleting(null);
      await refresh();
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    }
  }

  async function handleSymlink() {
    if (!symlinkFor) return;
    try {
      await symlinkMedia(symlinkFor.id, [...symlinkTargets]);
      setSymlinkFor(null);
      setSymlinkTargets(new Set());
      await refresh();
      toast.success("Symlink(s) created.");
    } catch {
      toast.error("Couldn't create the symlink.");
    }
  }

  function renderNodes(nodes: TreeNode[], depth: number) {
    return nodes.map((node, index) => {
      const Icon = TYPE_ICON[node.assetType];
      const isFolder = node.assetType === "DIR";
      const isCollapsed = collapsed.has(node.id);
      const canSymlink = node.assetType !== "DIR" && node.assetType !== "SLINK";
      return (
        <div key={node.id}>
          <div
            className="hover:bg-muted/40 flex items-center gap-2 rounded-md py-1.5 pr-2"
            style={{ paddingLeft: `${depth * 20 + 8}px` }}
          >
            {isFolder && node.children.length > 0 ? (
              <button type="button" onClick={() => toggle(node.id)} aria-label="Toggle">
                {isCollapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <Icon className="text-muted-foreground size-4 shrink-0" />
            <span className="truncate font-medium">{node.description}</span>
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {node.assetType}
            </Badge>
            <div className="ml-auto flex items-center gap-0.5">
              <Button variant="ghost" size="icon" aria-label="Move up" onClick={() => void moveSibling(nodes, index, -1)} disabled={index === 0}>
                <ArrowUp className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Move down" onClick={() => void moveSibling(nodes, index, 1)} disabled={index === nodes.length - 1}>
                <ArrowDown className="size-4" />
              </Button>
              {isFolder && (
                <Button variant="ghost" size="icon" aria-label="Add child" onClick={() => setNodeDialog({ node: null, parentId: node.id })}>
                  <Plus className="size-4" />
                </Button>
              )}
              {canSymlink && (
                <Button variant="ghost" size="icon" aria-label="Copy as symlink" onClick={() => { setSymlinkFor(node); setSymlinkTargets(new Set()); }}>
                  <Copy className="size-4" />
                </Button>
              )}
              {node.assetType !== "SLINK" && (
                <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => setNodeDialog({ node, parentId: node.parent })}>
                  <Pencil className="size-4" />
                </Button>
              )}
              <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => setDeleting(node)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
          {isFolder && !isCollapsed && node.children.length > 0 && renderNodes(node.children, depth + 1)}
        </div>
      );
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Media Library</CardTitle>
          <CardDescription>
            Nested tree of folders, videos, PDFs, links and text. Copy a leaf as a symlink to
            place it under multiple folders; deleting a folder removes its contents.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCollapsed(new Set())}>
            Expand all
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCollapsed(new Set(folders.map((f) => f.id)))}>
            Collapse all
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => setNodeDialog({ node: null, parentId: null })}>
            <Plus className="size-4" /> Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : tree.length === 0 ? (
          <p className="text-muted-foreground text-sm">No media nodes yet.</p>
        ) : (
          <div>{renderNodes(tree, 0)}</div>
        )}
      </CardContent>

      {nodeDialog && (
        <MediaNodeDialog
          node={nodeDialog.node}
          parentId={nodeDialog.parentId}
          open
          onOpenChange={(o) => !o && setNodeDialog(null)}
          onSaved={() => void refresh()}
        />
      )}

      {/* Symlink placement modal */}
      <Dialog open={!!symlinkFor} onOpenChange={(o) => !o && setSymlinkFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Copy “{symlinkFor?.description}” as a symlink</DialogTitle>
            <DialogDescription>
              Place a symlink copy of this asset under one or more folders. Existing links are
              skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {folders.length === 0 ? (
              <p className="text-muted-foreground text-sm">No folders to place it under.</p>
            ) : (
              folders.map((f) => (
                <div key={f.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`sym-${f.id}`}
                    checked={symlinkTargets.has(f.id)}
                    onCheckedChange={(c) =>
                      setSymlinkTargets((s) => {
                        const next = new Set(s);
                        if (c === true) next.add(f.id);
                        else next.delete(f.id);
                        return next;
                      })
                    }
                  />
                  <Label htmlFor={`sym-${f.id}`} className="cursor-pointer font-normal">
                    {f.description}
                  </Label>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button disabled={symlinkTargets.size === 0} onClick={() => void handleSymlink()}>
              Create symlink(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.description}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.assetType === "DIR"
                ? "This folder and everything inside it will be deleted."
                : deleting?.assetType === "SLINK"
                  ? "Only this symlink is removed; the original stays."
                  : "This node (and any symlinks to it) will be deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
